import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FestiveEffects,
  type FestivalType,
  type IntensityLevel,
  type ParticleType,
  type InteractiveMode,
  type FestiveEffectsHandle,
  getActiveFestival,
  IndependencePresets,
} from 'festive-effects';
import { Analytics } from '@vercel/analytics/react';
import './App.css';

interface Festival {
  id: FestivalType;
  name: string;
  emoji: string;
  description: string;
  colors: string[];
  particleTypes: ParticleType[];
}

const festivals: Festival[] = [
  { id: 'christmas', name: 'Christmas', emoji: '🎄', description: 'Snowflakes & gifts', colors: ['#FFFFFF', '#E8F4FF'], particleTypes: ['snowflake', 'gift', 'star'] },
  { id: 'newyear', name: 'New Year', emoji: '🎆', description: 'Fireworks & confetti', colors: ['#FFD700', '#FF6B6B'], particleTypes: ['firework', 'confetti', 'spark', 'ribbon'] },
  { id: 'valentine', name: 'Valentine', emoji: '💝', description: 'Floating hearts', colors: ['#FF6B6B', '#FF69B4'], particleTypes: ['heart', 'ribbon'] },
  { id: 'easter', name: 'Easter', emoji: '🐰', description: 'Eggs & flowers', colors: ['#FFB6C1', '#98FB98'], particleTypes: ['egg', 'flower'] },
  { id: 'halloween', name: 'Halloween', emoji: '🎃', description: 'Bats & pumpkins', colors: ['#FF6600', '#8B008B'], particleTypes: ['bat', 'pumpkin', 'spider'] },
  { id: 'thanksgiving', name: 'Thanksgiving', emoji: '🍂', description: 'Autumn leaves', colors: ['#D2691E', '#FF8C00'], particleTypes: ['leaf'] },
  { id: 'diwali', name: 'Diwali', emoji: '🪔', description: 'Diyas & sparkles', colors: ['#FFD700', '#FF8C00'], particleTypes: ['diya', 'spark', 'rangoli', 'cracker', 'candle'] },
  { id: 'chinesenewyear', name: 'Chinese NY', emoji: '🏮', description: 'Lanterns & dragons', colors: ['#FF0000', '#FFD700'], particleTypes: ['lantern', 'firework', 'dragon'] },
  { id: 'holi', name: 'Holi', emoji: '🎨', description: 'Color splashes', colors: ['#FF1493', '#00FF00'], particleTypes: ['color-splash'] },
  { id: 'eid', name: 'Eid', emoji: '🌙', description: 'Moons & stars', colors: ['#FFD700', '#228B22'], particleTypes: ['moon', 'star', 'candle'] },
  { id: 'stpatricks', name: "St. Patrick's", emoji: '☘️', description: 'Lucky shamrocks', colors: ['#228B22', '#FFD700'], particleTypes: ['shamrock', 'rainbow'] },
  { id: 'independence', name: 'Independence', emoji: '🎇', description: 'Patriotic fireworks', colors: ['#FF0000', '#0000FF'], particleTypes: ['firework', 'spark', 'confetti', 'cracker'] },
  { id: 'hanukkah', name: 'Hanukkah', emoji: '🕎', description: 'Menorahs & dreidels', colors: ['#1A5BB7', '#C0C0C0'], particleTypes: ['menorah', 'dreidel', 'star'] },
  { id: 'carnival', name: 'Carnival', emoji: '🎭', description: 'Masks & beads', colors: ['#FF00FF', '#FFD700'], particleTypes: ['mask', 'bead', 'confetti', 'ribbon'] },
];

const getCodeExample = (festival: string, intensity: string, particleTypes: ParticleType[], interactiveMode: InteractiveMode | null) => {
  const particleTypesStr = particleTypes.length > 0
    ? `\n      particleTypes={[${particleTypes.map(t => `'${t}'`).join(', ')}]}`
    : '';
  const interactiveModeStr = interactiveMode ? `\n      interactiveMode="${interactiveMode}"` : '';
  const refStr = interactiveMode ? `\n      ref={effectRef}` : '';
  return `import { FestiveEffects } from 'festive-effects';

function App() {
  return (
    <FestiveEffects
      festival="${festival}"
      intensity="${intensity}"${particleTypesStr}${interactiveModeStr}${refStr}
      duration={10000}
    />
  );
}`;
};

function App() {
  const [activeFestival, setActiveFestival] = useState<FestivalType>('christmas');
  const [intensity, setIntensity] = useState<IntensityLevel>('medium');
  const [isPlaying, setIsPlaying] = useState(true);
  const [copied, setCopied] = useState(false);
  const [selectedParticleTypes, setSelectedParticleTypes] = useState<ParticleType[]>([]);
  const [interactiveMode, setInteractiveMode] = useState<InteractiveMode | null>(null);
  const [independenceCountry, setIndependenceCountry] = useState<string>('US');
  const effectRef = useRef<FestiveEffectsHandle>(null);

  const autoFestival = getActiveFestival();

  const currentFestival = festivals.find(f => f.id === activeFestival)!;
  const availableParticleTypes = currentFestival.particleTypes;

  const codeExample = useMemo(() =>
    getCodeExample(activeFestival, intensity, selectedParticleTypes, interactiveMode),
    [activeFestival, intensity, selectedParticleTypes, interactiveMode]
  );

  const toggleParticleType = (type: ParticleType) => {
    setSelectedParticleTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText('npm install festive-effects');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePause = () => {
    effectRef.current?.pause();
    setIsPlaying(false);
  };

  const handleResume = () => {
    effectRef.current?.resume();
    setIsPlaying(true);
  };

  const handleRestart = () => {
    effectRef.current?.restart();
    setIsPlaying(true);
  };

  const activeIndependencePreset = activeFestival === 'independence'
    ? IndependencePresets[independenceCountry]
    : undefined;

  useEffect(() => {
    setIsPlaying(true);
    setSelectedParticleTypes([]);
    setInteractiveMode(null);
  }, [activeFestival]);

  return (
    <>
      <div className="app">
        {/* Background gradient orbs */}
        <div className="bg-orb bg-orb-1" />
        <div className="bg-orb bg-orb-2" />
        <div className="bg-orb bg-orb-3" />

        {/* Festive Effects */}
        <AnimatePresence mode="wait">
          {isPlaying && (
            <FestiveEffects
              ref={effectRef}
              key={`${activeFestival}-${selectedParticleTypes.join('-')}-${independenceCountry}`}
              festival={activeFestival}
              intensity={intensity}
              particleTypes={selectedParticleTypes.length > 0 ? selectedParticleTypes : undefined}
              colors={activeIndependencePreset?.colors}
              interactiveMode={interactiveMode ?? undefined}
              respectReducedMotion={false}
            />
          )}
        </AnimatePresence>

        {/* Hero Section */}
        <header className="hero">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="badge">
              <span className="badge-dot" />
              Professional React Library
            </div>

            <h1 className="hero-title">
              <span className="gradient-text">Festive Effects</span>
              <br />
              for React
            </h1>

            <p className="hero-subtitle">
              Beautiful, performant animations for every celebration.
              <br />
              Drop-in component. Zero config required.
            </p>

            <div className="hero-actions">
              <button className="btn btn-primary" onClick={copyToClipboard}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {copied ? (
                    <path d="M20 6L9 17l-5-5" />
                  ) : (
                    <>
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </>
                  )}
                </svg>
                {copied ? 'Copied!' : 'npm install festive-effects'}
              </button>
              {autoFestival && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setActiveFestival(autoFestival)}
                  title={`Today is ${autoFestival}!`}
                >
                  🎉 Today: {festivals.find(f => f.id === autoFestival)?.name ?? autoFestival}
                </button>
              )}
            </div>

            <div className="stats">
              <div className="stat">
                <span className="stat-value">14</span>
                <span className="stat-label">Festivals</span>
              </div>
              <div className="stat">
                <span className="stat-value">60</span>
                <span className="stat-label">FPS</span>
              </div>
              <div className="stat">
                <span className="stat-value">&lt;5kb</span>
                <span className="stat-label">Gzipped</span>
              </div>
            </div>
          </motion.div>
        </header>

        {/* Demo Section */}
        <section className="demo-section">
          <div className="container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2>Try it live</h2>
              <p>Click any festival to see the magic ✨</p>
            </motion.div>

            {/* Current Effect Display */}
            <motion.div
              className="current-effect"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="effect-emoji">{currentFestival.emoji}</div>
              <div className="effect-info">
                <h3>{currentFestival.name}</h3>
                <p>{currentFestival.description}</p>
              </div>
              <div className="effect-colors">
                {currentFestival.colors.map((color, i) => (
                  <span key={i} className="color-dot" style={{ background: color }} />
                ))}
              </div>
              <div className="effect-actions">
                <button
                  className={`action-btn ${isPlaying ? 'pause' : 'play'}`}
                  onClick={isPlaying ? handlePause : handleResume}
                  title={isPlaying ? 'Pause' : 'Resume'}
                >
                  {isPlaying ? '⏸' : '▶'}
                </button>
                <button
                  className="action-btn restart"
                  onClick={handleRestart}
                  title="Restart"
                >
                  ↺
                </button>
              </div>
            </motion.div>

            {/* Festival Grid */}
            <div className="festival-grid">
              {festivals.map((festival, index) => (
                <motion.button
                  key={festival.id}
                  className={`festival-card ${activeFestival === festival.id ? 'active' : ''}`}
                  onClick={() => setActiveFestival(festival.id)}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="festival-emoji">{festival.emoji}</span>
                  <span className="festival-name">{festival.name}</span>
                </motion.button>
              ))}
            </div>

            {/* Intensity Controls */}
            <div className="intensity-controls">
              <span className="intensity-label">Intensity:</span>
              {(['low', 'medium', 'high'] as IntensityLevel[]).map((level) => (
                <button
                  key={level}
                  className={`intensity-btn ${intensity === level ? 'active' : ''}`}
                  onClick={() => setIntensity(level)}
                >
                  {level === 'low' && '○'}
                  {level === 'medium' && '◐'}
                  {level === 'high' && '●'}
                  <span>{level}</span>
                </button>
              ))}
            </div>

            {/* Particle Type Controls */}
            <div className="particle-controls">
              <span className="intensity-label">Particles:</span>
              {availableParticleTypes.map((type) => (
                <button
                  key={type}
                  className={`particle-btn ${selectedParticleTypes.length === 0 || selectedParticleTypes.includes(type)
                    ? 'active'
                    : ''
                    }`}
                  onClick={() => toggleParticleType(type)}
                >
                  {type}
                </button>
              ))}
              {selectedParticleTypes.length > 0 && (
                <button
                  className="particle-btn reset"
                  onClick={() => setSelectedParticleTypes([])}
                >
                  ✕ Reset
                </button>
              )}
            </div>

            {/* Interactive Mode Controls */}
            <div className="interactive-controls">
              <span className="intensity-label">Interactive:</span>
              {([null, 'onHover', 'onClick', 'follow', 'repel'] as (InteractiveMode | null)[]).map((mode) => (
                <button
                  key={mode ?? 'none'}
                  className={`intensity-btn ${interactiveMode === mode ? 'active' : ''}`}
                  onClick={() => setInteractiveMode(mode)}
                >
                  {mode === null && '—'}
                  {mode === 'onHover' && '🖱'}
                  {mode === 'onClick' && '👆'}
                  {mode === 'follow' && '🎯'}
                  {mode === 'repel' && '💫'}
                  <span>{mode === 'onHover' ? 'hover' : mode === 'onClick' ? 'click' : mode ?? 'none'}</span>
                  {mode === 'repel' && <span className="badge-new">NEW</span>}
                </button>
              ))}
            </div>

            {/* Independence Country Presets */}
            {activeFestival === 'independence' && (
              <div className="country-controls">
                <span className="intensity-label">Country:</span>
                {Object.keys(IndependencePresets).map((code) => (
                  <button
                    key={code}
                    className={`country-btn ${independenceCountry === code ? 'active' : ''}`}
                    onClick={() => setIndependenceCountry(code)}
                  >
                    <span
                      className="country-dot"
                      style={{ background: IndependencePresets[code].colors[0] }}
                    />
                    {IndependencePresets[code].country}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Code Section */}
        <section className="code-section">
          <div className="container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2>Simple to use</h2>
              <p>Just one component, endless celebrations</p>
            </motion.div>

            <motion.div
              className="code-block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="code-header">
                <div className="code-dots">
                  <span />
                  <span />
                  <span />
                </div>
                <span className="code-filename">App.tsx</span>
              </div>
              <pre><code>{codeExample}</code></pre>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <div className="container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2>Why Festive Effects?</h2>
            </motion.div>

            <div className="features-grid">
              {[
                { icon: '⚡', title: 'Performant', desc: 'GPU-accelerated animations at 60fps' },
                { icon: '🎨', title: '14 Festivals', desc: 'From Christmas to Carnival and beyond' },
                { icon: '📦', title: 'Lightweight', desc: 'Tree-shakeable, minimal bundle impact' },
                { icon: '♿', title: 'Accessible', desc: 'Respects prefers-reduced-motion' },
                { icon: '🔧', title: 'Customizable', desc: 'Intensity, duration, colors & more' },
                { icon: '💪', title: 'TypeScript', desc: 'Full type safety out of the box' },
                { icon: '🖱', title: 'Interactive', desc: 'Hover, click, follow & repel modes' },
                { icon: '🎯', title: 'Auto-detect', desc: 'Automatically picks today\'s festival' },
              ].map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="feature-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <span className="feature-icon">{feature.icon}</span>
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <motion.div
              className="cta-content"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2>Ready to celebrate?</h2>
              <p>Add festive magic to your React app in seconds</p>
              <button className="btn btn-primary btn-large" onClick={copyToClipboard}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {copied ? (
                    <path d="M20 6L9 17l-5-5" />
                  ) : (
                    <>
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </>
                  )}
                </svg>
                {copied ? 'Copied!' : 'npm install festive-effects'}
              </button>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">

            <p className="footer-links">
              <a href="https://www.npmjs.com/package/festive-effects">npm</a>
              <span>Harshit-d3v</span>
            </p>
          </div>
        </footer>
      </div>
      <Analytics />
    </>
  );
}

export default App;
